import { Table } from '@billy/design-system'
import { getMonth, getYear } from 'date-fns'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Row } from 'react-table'

import { useAccount } from '@features/accounts/hooks/useAccount'
import { useTransactionView } from '@features/transactions/contexts/transactionViewContext'

import { SortDirection } from '../../../../enums/sortDirection'
import { TableIds } from '../../../../enums/tableIds'
import { AccountTableRow } from '../../enums/accountTableRow'
import { useAccountFilters } from '../../hooks/useAccountFilters'
import { useAccountStatement } from '../../hooks/useAccountStatement'
import { AccountSortProperty } from '../../types/accountSortProperty'
import { ChartOfAccountsUrlParams } from '../../types/chartOfAccountsUrlParams'
import { getAccountTableColumns } from '../../utils/getAccountTableColumns'
import { getAccountTableData } from '../../utils/getAccountTableData'

export const AccountTable = (): ReactElement => {
  const { t } = useTranslation()
  const { accountId } = useParams<ChartOfAccountsUrlParams>()
  const { account } = useAccount(accountId)
  const [{ page, pageSize, sortProperty, sortDirection, withVoided }, setQueryParams] = useAccountFilters()
  const { accountStatement, isLoading } = useAccountStatement()
  const { handleTransactionClick } = useTransactionView()

  const columns = useMemo(
    () => getAccountTableColumns(withVoided, account?.currencyId, t),
    [account?.currencyId, t, withVoided],
  )
  const data = useMemo(() => (accountStatement ? getAccountTableData(accountStatement) : []), [accountStatement])

  const sortedColumnId = useMemo(() => {
    if (!sortProperty) {
      return undefined
    }

    return sortDirection === SortDirection.Desc ? `-${sortProperty}` : `${sortProperty}`
  }, [sortDirection, sortProperty])

  const handlePageChange = useCallback(
    (paginationPage: number) => {
      setQueryParams({ page: paginationPage })
    },
    [setQueryParams],
  )

  const handleRowClick = useCallback(
    (row: Row<AccountTableRow>) => {
      const { transactionId, transactionNo } = row.original

      if (!transactionId) {
        return
      }

      handleTransactionClick({ id: transactionId, transactionNo })
    },
    [handleTransactionClick],
  )

  const handleSort = useCallback(
    (columnId: string, isDesc: boolean) => {
      setQueryParams({
        page: 1,
        sortProperty: columnId as AccountSortProperty,
        sortDirection: isDesc ? SortDirection.Desc : SortDirection.Asc,
      })
    },
    [setQueryParams],
  )

  const groupRowsByMonths = useCallback((row: Row<AccountTableRow>) => {
    const { entryDate } = row.original

    if (!entryDate) {
      return ''
    }

    const year = getYear(entryDate)
    const month = getMonth(entryDate)
    return `${year}-${month}`
  }, [])

  return (
    <Table
      columns={columns}
      currentPage={page}
      data={data}
      groupRowsBy={groupRowsByMonths}
      id={TableIds.AccountStatementsTable}
      isClientSidePagination
      isLoading={isLoading}
      itemsPerPage={pageSize}
      onPageChange={handlePageChange}
      onRowClick={handleRowClick}
      onSort={handleSort}
      sortedColumnId={sortedColumnId}
      withColumnsFiltering
      withPagination
      withStickyHeader
    />
  )
}
